import React from 'react';
import { useTranslation } from 'react-i18next';
import { intervalSavingsPercent, type SubscriptionInterval } from '@hero/core';
import type { Currency } from '@hero/schema';
import { cn } from '@hero/web-lib';

export interface IntervalToggleProps {
  value: SubscriptionInterval;
  onChange: (interval: SubscriptionInterval) => void;
  /**
   * Which plan's discount curve the badges describe. Discounts are
   * per-plan in the catalog, so the badge follows whatever the user is
   * actually looking at.
   */
  savingsPlanCode?: string;
  currency?: Currency;
  className?: string;
}

const INTERVAL_IDS: SubscriptionInterval[] = ['month', 'semester', 'year'];

/**
 * Segmented pill control for picking the billing interval.
 *
 * Each non-monthly pill carries an "X% OFF" badge so the user can
 * compare 6-month vs yearly discounts inline without a separate
 * headline label above the toggle.
 *
 * The percentage is derived from the plan's own rates in
 * `SUBSCRIPTION_CONFIG` rather than written here. It used to be a
 * hardcoded 15 / 30 "marketing label", which meant editing a price
 * silently turned the badge into a lie — the number promised and the
 * number charged came from two different files.
 */
export function IntervalToggle({
  value,
  onChange,
  savingsPlanCode = 'starter',
  currency = 'SAR',
  className,
}: IntervalToggleProps) {
  const { t } = useTranslation('m-billing');

  return (
    <div className={cn('flex flex-col items-start', className)}>
      <div className="inline-flex rounded-full border border-gray-200 bg-white p-1 shadow-sm">
        {INTERVAL_IDS.map(id => {
          const active = id === value;
          const savings = intervalSavingsPercent(savingsPlanCode, id, currency);
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition',
                active
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900',
              )}>
              <span>{t(`interval_toggle.${id}`)}</span>
              {savings !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide',
                    active
                      ? 'bg-white/25 text-white'
                      : 'bg-emerald-100 text-emerald-700',
                  )}>
                  {t('interval_toggle.savings_badge', { percentage: savings })}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

